import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useGetList, useGetMany, useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { ListSkeleton } from "../misc/ListSkeleton";
import { formatRelativeDate } from "../misc/RelativeDate";
import type { Contact, ContactNote } from "../types";
import { plainText } from "./mobileModel";
import { usePersonName } from "../misc/usePersonName";

const STATUS_BAR: Record<string, string> = {
  cold: "bg-chart-1",
  warm: "bg-chart-2",
  hot: "bg-chart-5",
  "in-contract": "bg-success",
};

export const MobileMessages = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const personName = usePersonName();
  const [locale = "es"] = useLocaleState();
  const { data: notes, isPending } = useGetList<ContactNote>("contact_notes", {
    pagination: { page: 1, perPage: 40 },
    sort: { field: "date", order: "DESC" },
  });
  const contactIds = [...new Set((notes ?? []).map((note) => note.contact_id))];
  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );
  return (
    <>
      <MobileHeader>
        <h1 className="flex-1 text-2xl font-semibold tracking-tight">
          {translate("crm.mobile.messages_title")}
        </h1>
      </MobileHeader>
      <MobileContent>
        {isPending ? (
          <ListSkeleton variant="cards" rows={4} />
        ) : !notes?.length ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.mobile.messages_empty")}
          </p>
        ) : (
          <div className="flex flex-col gap-3" data-messages-list>
            {notes.map((note) => {
              const contact = contacts?.find(
                (item) => item.id === note.contact_id,
              );
              const name = contact
                ? personName(contact)
                : translate("crm.common.unnamed");
              return (
                <Link
                  key={note.id}
                  to={
                    isMobile
                      ? `/contacts/${note.contact_id}/notes/${note.id}`
                      : `/contacts/${note.contact_id}/show`
                  }
                  className="rounded-4xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <Card className="h-full rounded-4xl">
                    <CardContent className="flex items-stretch gap-3">
                      <span
                        aria-hidden
                        className={cn(
                          "w-1 self-stretch rounded-full",
                          STATUS_BAR[note.status] ?? "bg-chart-1",
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          {contacts || contactIds.length === 0 ? (
                            <span className="truncate font-medium">{name}</span>
                          ) : (
                            <Skeleton className="h-4 w-32" />
                          )}
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {formatRelativeDate(note.date, locale)}
                          </span>
                        </span>
                        <span className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                          {plainText(note.text, 400)}
                        </span>
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </MobileContent>
    </>
  );
};
